import React, { useEffect, useState } from "react";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Modal,
  Fade,
  Backdrop,
  Chip,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import {
  Person,
  Public,
  Phone,
  Email,
  Visibility,
  Delete,
  ChevronLeft,
  ChevronRight,
  Close,
  Reply,
  MailOutline,
} from "@mui/icons-material";
import axios from "axios";
import "./contactMessages.scss";

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const messagesPerPage = 10;

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get("https://api.moroccool.com/api/contact");
      setMessages(res.data.reverse());
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = messages.filter((msg) => {
    if (filter === "All") return true;
    if (filter === "Seen") return msg.seen;
    if (filter === "Unseen") return !msg.seen;
    if (filter === "Normal Client") return msg.clientType === "Traveler";
    if (filter === "Client with Business") return msg.clientType === "Client with Business";
    return true;
  });

  const totalPages = Math.ceil(filtered.length / messagesPerPage);
  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = filtered.slice(indexOfFirstMessage, indexOfLastMessage);

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handleOpenModal = (msg) => {
    if (!msg.seen) {
      axios.patch(`https://api.moroccool.com/api/contact/${msg._id}/seen`, { seen: true }).then(() => {
        setMessages((prev) =>
          prev.map((m) => (m._id === msg._id ? { ...m, seen: true } : m))
        );
      });
    }
    setSelectedMsg(msg);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMsg(null);
  };

  const handleReplyOpen = () => setReplyOpen(true);
  const handleReplyClose = () => {
    setReplyOpen(false);
    setReplyContent("");
  };

  const sendReply = async () => {
    if (!replyContent.trim()) return alert("Please write your reply message.");
    try {
      await axios.post("https://api.moroccool.com/api/reply", {
        to: selectedMsg.email,
        message: replyContent,
        subject: `Re: Your message to us`,
      });
      alert("Reply sent successfully!");
      handleReplyClose();
    } catch (error) {
      alert("Failed to send reply.");
      console.error(error);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    try {
      const res = await fetch(`https://api.moroccool.com/api/contact/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setMessages((prev) => prev.filter((msg) => msg._id !== id));
        if (currentMessages.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } else {
        console.error("Failed to delete");
      }
    } catch (err) {
      console.error("Error deleting message:", err);
    }
  };

  const getInitials = (firstName, lastName) =>
    `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();

  const unreadCount = messages.filter((m) => !m.seen).length;

  return (
    <Box className="contact-dashboard">
      <Sidebar />
      <Box className="contact-main">
        <Navbar />

        <Box className="contact-content">
          {/* Header */}
          <Box className="content-header">
            <Box className="header-left">
              <Box className="header-title-row">
                <Typography className="page-title">Inbox</Typography>
                {unreadCount > 0 && (
                  <span className="unread-badge">{unreadCount} new</span>
                )}
              </Box>
              <Typography className="page-subtitle">
                {filtered.length} {filter === "All" ? "total" : ""} messages
              </Typography>
            </Box>

            <Box className="header-right">
              <Box className="filter-tabs">
                {["All", "Unseen", "Seen", "Normal Client", "Client with Business"].map((f) => {
                  const labels = {
                    All: "All",
                    Unseen: "Unread",
                    Seen: "Read",
                    "Normal Client": "Traveler",
                    "Client with Business": "Business",
                  };
                  return (
                    <button
                      key={f}
                      className={`filter-tab ${filter === f ? "active" : ""}`}
                      onClick={() => { setFilter(f); setCurrentPage(1); }}
                    >
                      {labels[f]}
                    </button>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Table */}
          {filtered.length === 0 ? (
            <Box className="empty-state">
              <MailOutline className="empty-icon" />
              <Typography className="empty-title">No messages</Typography>
              <Typography className="empty-sub">Nothing here yet.</Typography>
            </Box>
          ) : (
            <>
              <Box className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th className="col-status"></th>
                      <th className="col-name">Sender</th>
                      <th className="col-type">Type</th>
                      <th className="col-country">Country</th>
                      <th className="col-phone">Phone</th>
                      <th className="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentMessages.map((msg) => (
                      <tr
                        key={msg._id}
                        className={`table-row ${!msg.seen ? "unread" : ""}`}
                        onClick={() => handleOpenModal(msg)}
                      >
                        <td className="col-status">
                          <span className={`dot ${msg.seen ? "seen" : "unseen"}`} />
                        </td>
                        <td className="col-name">
                          <Box className="sender-cell">
                            <div className="avatar">{getInitials(msg.firstName, msg.lastName)}</div>
                            <Box className="sender-info">
                              <span className="sender-name">
                                {msg.firstName} {msg.lastName}
                              </span>
                              <span className="sender-email">{msg.email}</span>
                            </Box>
                          </Box>
                        </td>
                        <td className="col-type">
                          <span
                            className={`type-chip ${msg.clientType === "Client with Business"
                              ? "chip-business"
                              : "chip-traveler"
                              }`}
                          >
                            {msg.clientType === "Client with Business" ? "Business" : "Traveler"}
                          </span>
                        </td>
                        <td className="col-country">{msg.country || "—"}</td>
                        <td className="col-phone">{msg.phone || "—"}</td>
                        <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            className="icon-btn"
                            onClick={() => handleOpenModal(msg)}
                          >
                            <Visibility sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            className="icon-btn danger"
                            onClick={(e) => handleDelete(msg._id, e)}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {totalPages > 1 && (
                <Box className="pagination">
                  <span className="page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Box className="page-controls">
                    <button
                      className="page-arrow"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <ChevronLeft sx={{ fontSize: 18 }} />
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        className={`page-num ${currentPage === i + 1 ? "active" : ""}`}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      className="page-arrow"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      <ChevronRight sx={{ fontSize: 18 }} />
                    </button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Detail Modal */}
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 250, style: { backgroundColor: "rgba(15,23,42,0.4)" } } }}
        >
          <Fade in={modalOpen}>
            <Box className="detail-modal">
              {selectedMsg && (
                <>
                  <Box className="modal-header">
                    <Typography className="modal-title">Message</Typography>
                    <IconButton className="modal-close" onClick={handleCloseModal} size="small">
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>

                  <Box className="modal-sender-row">
                    <div className="modal-avatar">
                      {getInitials(selectedMsg.firstName, selectedMsg.lastName)}
                    </div>
                    <Box>
                      <Typography className="modal-sender-name">
                        {selectedMsg.firstName} {selectedMsg.lastName}
                      </Typography>
                      <Typography className="modal-sender-email">
                        {selectedMsg.email}
                      </Typography>
                    </Box>
                    <span
                      className={`type-chip ml-auto ${selectedMsg.clientType === "Client with Business"
                        ? "chip-business"
                        : "chip-traveler"
                        }`}
                    >
                      {selectedMsg.clientType === "Client with Business" ? "Business" : "Traveler"}
                    </span>
                  </Box>

                  <Box className="modal-meta-grid">
                    <Box className="meta-item">
                      <Phone sx={{ fontSize: 14 }} />
                      <span>{selectedMsg.phone || "No phone"}</span>
                    </Box>
                    <Box className="meta-item">
                      <Public sx={{ fontSize: 14 }} />
                      <span>{selectedMsg.country || "Unknown country"}</span>
                    </Box>
                  </Box>

                  <Box className="modal-body">
                    <Typography className="body-label">Message</Typography>
                    <Typography className="body-text">{selectedMsg.message}</Typography>
                  </Box>

                  <Box className="modal-footer">
                    <Button
                      variant="outlined"
                      className="footer-btn danger"
                      onClick={(e) => {
                        handleDelete(selectedMsg._id, e);
                        handleCloseModal();
                      }}
                      startIcon={<Delete sx={{ fontSize: 15 }} />}
                    >
                      Delete
                    </Button>
                    <Box className="footer-right">
                      <Button
                        variant="outlined"
                        className="footer-btn secondary"
                        onClick={handleCloseModal}
                      >
                        Close
                      </Button>
                      <Button
                        variant="contained"
                        className="footer-btn primary"
                        onClick={handleReplyOpen}
                        startIcon={<Reply sx={{ fontSize: 15 }} />}
                      >
                        Reply
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Fade>
        </Modal>

        {/* Reply Modal */}
        <Modal
          open={replyOpen}
          onClose={handleReplyClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 250, style: { backgroundColor: "rgba(15,23,42,0.4)" } } }}
        >
          <Fade in={replyOpen}>
            <Box className="reply-modal">
              <Box className="modal-header">
                <Typography className="modal-title">Reply</Typography>
                <IconButton className="modal-close" onClick={handleReplyClose} size="small">
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              <Box className="reply-to-row">
                <span className="reply-label">To</span>
                <span className="reply-address">{selectedMsg?.email}</span>
              </Box>

              <TextField
                multiline
                rows={8}
                fullWidth
                variant="outlined"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write your reply…"
                autoFocus
                className="reply-field"
              />

              <Box className="modal-footer">
                <Box className="footer-right">
                  <Button
                    variant="outlined"
                    className="footer-btn secondary"
                    onClick={handleReplyClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    className="footer-btn primary"
                    onClick={sendReply}
                    startIcon={<Reply sx={{ fontSize: 15 }} />}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </Box>
          </Fade>
        </Modal>
      </Box>
    </Box>
  );
};

export default ContactMessages;